"use client";

import { differenceInCalendarDays, format, parseISO } from "date-fns";
import { srLatn } from "date-fns/locale";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { parseAsStringEnum, useQueryState } from "nuqs";
import { TherapyRelationshipType } from "~/prisma/browser";
import { resolveTherapyTypeQuery } from "~/lib/therapy-type-query";
import { cn } from "~/lib/utils";
import { buttonVariants } from "~/components/ui/button";

import {
  DailyAvailabilityProvider,
  useDailyAvailability,
} from "./daily-availability-context";
import { countWithNoun, type PluralForms } from "./therapist-card/types";
import { TherapistClientStatus } from "./therapist-client-status";

const FREE_SLOT_FORMS: PluralForms = {
  one: "termin",
  few: "termina",
  many: "termina",
};

interface TherapistAvailabilityPreviewProps {
  slug: string;
  therapyType: "INDIVIDUAL" | "COUPLES";
  profileHref: string;
  days: Array<{ date: string; slotCount: number }>;
  preserveCouplesQuery?: boolean;
  layout?: "card" | "profile";
}

export function TherapistAvailabilityPreview({
  slug,
  therapyType,
  profileHref,
  days,
  preserveCouplesQuery = false,
  layout = "card",
}: TherapistAvailabilityPreviewProps) {
  const hasFreeDay = days.some((day) => day.slotCount > 0);
  const today = new Date();

  return (
    <div>
      <p className="text-muted-foreground mb-3 text-sm">
        {hasFreeDay
          ? "Izaberi datum da vidiš tačne termine"
          : "Nema slobodnih termina ove nedelje"}
      </p>
      <div
        className={cn(
          "bg-editorial-hairline border-editorial-hairline grid grid-cols-4 gap-px overflow-hidden rounded-xl border",
          layout === "profile" && "xl:grid-cols-8",
        )}
      >
        {days.map(({ date, slotCount }) => {
          const parsedDate = parseISO(date);
          const daysFromToday = differenceInCalendarDays(parsedDate, today);
          const dayLabel =
            daysFromToday === 0
              ? "danas"
              : daysFromToday === 1
                ? "sutra"
                : format(parsedDate, "EEE", { locale: srLatn });
          const availabilityLabel =
            slotCount === 0
              ? "Nema termina"
              : countWithNoun(slotCount, FREE_SLOT_FORMS);
          const fullDateLabel = `${format(parsedDate, "EEEE, d. MMMM", { locale: srLatn })}, ${availabilityLabel}`;
          const dateContent = (
            <>
              <span className="text-muted-foreground text-[0.625rem] leading-none font-semibold uppercase">
                {dayLabel}
              </span>
              <time
                dateTime={date}
                className="mt-1.5 text-base leading-none font-semibold tabular-nums"
              >
                {format(parsedDate, "d.M.", { locale: srLatn })}
              </time>
              <span
                className={cn(
                  "mt-1.5 inline-flex h-5 items-center justify-center rounded-full px-1 text-[0.6875rem] leading-none font-medium whitespace-nowrap tabular-nums",
                  slotCount > 0
                    ? "bg-brand/10 text-brand"
                    : "text-muted-foreground",
                )}
                title={availabilityLabel}
              >
                {slotCount > 0 ? (
                  availabilityLabel
                ) : (
                  <>
                    <span aria-hidden="true">−</span>
                    <span className="sr-only">{availabilityLabel}</span>
                  </>
                )}
              </span>
            </>
          );

          if (slotCount === 0) {
            return (
              <div
                key={date}
                aria-label={fullDateLabel}
                className="bg-background text-foreground flex min-h-20 flex-col items-center justify-center px-1 py-2 text-center"
              >
                <div className="text-muted-foreground flex flex-col items-center opacity-60">
                  {dateContent}
                </div>
              </div>
            );
          }

          return (
            <Link
              key={date}
              aria-label={fullDateLabel}
              href={`/terapeuti/${slug}/book?therapyType=${therapyType}&initialDate=${date}${
                preserveCouplesQuery ? "&couples=1" : ""
              }`}
              prefetch={false}
              className={cn(
                buttonVariants({ variant: "outline", size: null }),
                "min-h-20 w-full flex-col gap-0 rounded-none border-0 px-1 py-2 text-center focus-visible:z-10 focus-visible:ring-offset-0 focus-visible:ring-inset",
              )}
            >
              {dateContent}
            </Link>
          );
        })}
      </div>
      {!hasFreeDay && (
        <p className="mt-3 text-center">
          <Link
            href={profileHref}
            prefetch={false}
            className="text-brand text-sm font-medium hover:underline"
          >
            Pogledaj kasnije datume na profilu
          </Link>
        </p>
      )}
      <Link
        href={`/terapeuti/${slug}/book?therapyType=${therapyType}${
          preserveCouplesQuery ? "&couples=1" : ""
        }`}
        prefetch={false}
        className="text-brand hover:text-brand-action-hover focus-visible:ring-ring mt-3 ml-auto flex w-fit items-center gap-1.5 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden"
      >
        Pogledaj sve termine
        <ArrowRight className="size-3.5" aria-hidden="true" />
      </Link>
    </div>
  );
}

export function AvailabilityPreviewSkeleton() {
  return (
    <div aria-label="Učitavanje termina">
      <div className="bg-secondary mb-3 h-5 w-56 animate-pulse rounded" />
      <div className="bg-editorial-hairline border-editorial-hairline grid grid-cols-4 gap-px overflow-hidden rounded-xl border xl:grid-cols-8">
        {Array.from({ length: 8 }, (_, index) => (
          <div key={index} className="bg-secondary h-20 animate-pulse" />
        ))}
      </div>
      <div className="bg-secondary mt-3 ml-auto h-5 w-36 animate-pulse rounded" />
    </div>
  );
}

// loading-boundary-exception: This child owns only availability dates and states; the profile heading, portrait, pricing, and primary booking controls stay visible in the parent.
function ProfileAvailabilityPreviewContent({
  slug,
  therapyType,
}: Pick<TherapistAvailabilityPreviewProps, "slug" | "therapyType">) {
  const availability = useDailyAvailability(slug);

  if (availability.state === "loading") {
    return <AvailabilityPreviewSkeleton />;
  }

  if (availability.state !== "ready") return null;

  return (
    <TherapistAvailabilityPreview
      layout="profile"
      slug={slug}
      therapyType={therapyType}
      profileHref={`/terapeuti/${slug}${
        therapyType === TherapyRelationshipType.COUPLES ? "?couples=1" : ""
      }`}
      days={availability.days}
      preserveCouplesQuery={therapyType === TherapyRelationshipType.COUPLES}
    />
  );
}

export function ProfileAvailabilityPreview({
  isIncognito = false,
  ...props
}: {
  slug: string;
  offersCouplesTherapy?: boolean;
  isIncognito?: boolean;
}) {
  if (isIncognito) {
    return <TherapistClientStatus slug={props.slug} showActions />;
  }
  return <PublicProfileAvailabilityPreview {...props} />;
}

function PublicProfileAvailabilityPreview({
  slug,
  offersCouplesTherapy = false,
}: Pick<TherapistAvailabilityPreviewProps, "slug"> & {
  offersCouplesTherapy?: boolean;
}) {
  const searchParams = useSearchParams();
  const defaultTherapyType = offersCouplesTherapy
    ? resolveTherapyTypeQuery({
        therapyType: searchParams.get("therapyType"),
        couples: searchParams.get("couples"),
      })
    : TherapyRelationshipType.INDIVIDUAL;
  const [therapyType] = useQueryState(
    "therapyType",
    parseAsStringEnum<TherapyRelationshipType>(
      Object.values(TherapyRelationshipType),
    ).withDefault(defaultTherapyType),
  );

  return (
    <DailyAvailabilityProvider
      therapistSlugs={[slug]}
      sessionType={therapyType}
    >
      <ProfileAvailabilityPreviewContent
        slug={slug}
        therapyType={therapyType}
      />
    </DailyAvailabilityProvider>
  );
}
